// pages/solutions/CreativeTeams.jsx
import { useNavigate } from 'react-router-dom';

export default function CreativeTeams() {
  const navigate = useNavigate();

  const features = [
    {
      icon: "🎨",
      title: "AI Creative Assistant",
      description: "Generate variations and adaptations instantly with AI-powered tools."
    },
    {
      icon: "⚡",
      title: "Batch Production",
      description: "Create hundreds of ad variations in minutes, not days."
    },
    {
      icon: "🔄",
      title: "Auto-Resizing",
      description: "Automatically adapt designs for all platform sizes and formats."
    },
    {
      icon: "💾",
      title: "Asset Library",
      description: "Organize and access all your creative assets in one place."
    },
    {
      icon: "🤝",
      title: "Team Collaboration",
      description: "Work together seamlessly with real-time feedback and versioning."
    },
    {
      icon: "📊",
      title: "Creative Analytics",
      description: "See which creative elements drive the best performance."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black text-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#00809D]/20 via-transparent to-transparent pointer-events-none" />
        
        <div className="max-w-6xl mx-auto relative">
          <button 
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back
          </button>

          <div className="text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
              Creative Teams
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Focus on big ideas while AI handles production at scale, so you can stop being a human file converter.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-8 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
                Try It Free
              </button>
              <button className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-xl font-semibold hover:bg-white/20 transition-all">
                Watch Demo
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            Creative Superpowers
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-6 hover:bg-white/10 hover:border-white/20 transition-all group"
              >
                <div className="text-4xl mb-4 group-hover:scale-110 transition-transform">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Time Savings Section */}
      <section className="py-20 px-6 bg-gradient-to-b from-[#00809D]/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-8">
            Reclaim Your Creative Time
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">90%</div>
              <div className="text-gray-400">Less Time on Resizing</div>
            </div>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">5x</div>
              <div className="text-gray-400">Faster Production</div>
            </div>
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl p-8">
              <div className="text-5xl font-bold text-[#00a0c4] mb-2">100+</div>
              <div className="text-gray-400">Variations Per Hour</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-r from-[#00809D]/20 to-[#00a0c4]/20 backdrop-blur-lg border border-white/20 rounded-3xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Free Your Creative Genius
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Let AI handle the tedious work. You focus on what you do best.
          </p>
          <button className="bg-gradient-to-r from-[#00809D] to-[#00a0c4] text-white px-10 py-4 rounded-xl font-semibold shadow-lg shadow-[#00809D]/50 hover:shadow-xl hover:shadow-[#00809D]/70 hover:scale-105 transition-all">
            Start Creating
          </button>
        </div>
      </section>
    </div>
  );
}
